<template>
  <div>
    <Modal
      :title="modalTitle"
      :saving="saving"
      @hide-modal="emit('hide-modal')"
      @submit="handleSave"
    >
      <CustomerMembershipForm
        ref="form"
        :membership="membership"
        :customer-id="currentUser?.id"
      />
      <template #delete>
        <Button
          v-if="membership.membership_delete_url"
          type="Alert"
          label="Delete"
          @click="confirmOpen = true"
        />
      </template>
    </Modal>
    <Modal
      title="Delete Membership"
      :open="confirmOpen"
      @hide-modal="confirmOpen = !confirmOpen"
    >
      <div>Are you sure you want to delete this customers membership?</div>
      <template #submit>
        <Button type="Alert" :disabled="deleting" @click="handleDelete">
          <Spinner v-if="deleting" size="small" />
          <span v-else>Delete</span>
        </Button>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import CustomerMembershipForm from "./CustomerMembershipForm.vue";

const store = useStore();
const currentUser = computed(() => store.state.currentUser);

const props = defineProps<{
  membership: Record<string, unknown>;
}>();

const emit = defineEmits<{
  "action-success": [];
  "hide-modal": [];
}>();

const form = ref<InstanceType<typeof CustomerMembershipForm> | null>(null);
const saving = ref(false);
const deleting = ref(false);
const confirmOpen = ref(false);

const modalTitle = computed(() =>
  props.membership.membership_id ? "Edit Membership" : "Add Membership"
);

onMounted(() => {
  store.dispatch("fetchMembershipPrograms");
});

function handleSave() {
  form.value?.getFormData().then((formData: Record<string, unknown>) => {
    handleAction({
      customerId: currentUser.value.id,
      data: { membership: formData }
    });
  });
}

function handleDelete() {
  deleting.value = true;
  store
    .dispatch("deleteCustomerMembership", {
      url: props.membership.membership_delete_url
    })
    .then(() => {
      emit("action-success");
      (window as { notify: { success: (msg: string) => void } }).notify.success(
        "Membership deleted successfully!"
      );
      emit("hide-modal");
    })
    .finally(() => {
      deleting.value = false;
    });
}

function handleAction(payload: {
  customerId: number;
  data: { membership: Record<string, unknown> };
  url?: string;
}) {
  saving.value = true;
  let actionName = "createCustomerMembership";
  let label = "created";

  if (payload.data.membership.membership_id) {
    actionName = "updateCustomerMembership";
    label = "updated";
    payload.url = props.membership.membership_update_url as string;
  }

  store
    .dispatch(actionName, payload)
    .then(() => {
      emit("action-success");
      (window as { notify: { success: (msg: string) => void } }).notify.success(
        `Membership ${label} successfully!`
      );
      emit("hide-modal");
    })
    .finally(() => {
      saving.value = false;
    });
}
</script>

<style lang="scss" scoped></style>
